import {gql} from '@apollo/client'

export const categoryFragment = gql`
  fragment CategoryProps on Category {
    id
    slug
    name
    products {
      totalCount
    }
  }
`

export const getCategoriesQuery = gql`
  ${categoryFragment}
  query GetCategories {
    categories(first: 100, level: 0) {
      edges {
        node {
          ...CategoryProps
          children(first: 100) {
            edges {
              node {
                ...CategoryProps
                children(first: 100) {
                  edges {
                    node {
                      ...CategoryProps
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const priceFragment = gql`
  fragment Price on TaxedMoney {
    gross {
      amount
      currency
    }
    net {
      amount
    }
    gross {
      amount
    }
  }
`

export const basicProductFragment = gql`
  fragment BasicProductFields on Product {
    id
    name
    thumbnail {
      url
      alt
    }
    thumbnail2x: thumbnail(size: 510) {
      url
    }
  }
`

export const productPricingFragment = gql`
  ${priceFragment}
  fragment ProductPricingField on Product {
    pricing {
      onSale
      priceRangeUndiscounted {
        start {
          ...Price
        }
        stop {
          ...Price
        }
      }
      priceRange {
        start {
          ...Price
        }
        stop {
          ...Price
        }
      }
    }
  }
`

export const selectedAttributeFragment = gql`
  fragment SelectedAttributeFields on SelectedAttribute {
    attribute {
      id
      name
    }
    values {
      id
      name
    }
  }
`

export const productVariantFragment = gql`
  ${priceFragment}
  fragment ProductVariantFields on ProductVariant {
    id
    sku
    name
    quantityAvailable
    images {
      id
      alt
      url: url(size: 600)
      url2x: url(size: 1200)
    }
    pricing {
      onSale
      priceUndiscounted {
        ...Price
      }
      price {
        ...Price
      }
    }
    attributes {
      attribute {
        id
        name
        slug
      }
      values {
        id
        name
        value: name
      }
    }
  }
`

export const productDetailsQuery = gql`
  ${basicProductFragment}
  ${selectedAttributeFragment}
  ${productVariantFragment}
  ${productPricingFragment}
  query ProductDetails($slug: String!) {
    product(slug: $slug) {
      ...BasicProductFields
      ...ProductPricingField
      descriptionJson
      category {
        id
        name
        products(first: 3) {
          edges {
            node {
              ...BasicProductFields
              ...ProductPricingField
            }
          }
        }
      }
      images {
        id
        alt
        url: url(size: 600)
        url2x: url(size: 1200)
      }
      attributes {
        ...SelectedAttributeFields
      }
      variants {
        ...ProductVariantFields
      }
      seoDescription
      seoTitle
      isAvailable
    }
  }
`

export const productVariantsQuery = gql`
  ${basicProductFragment}
  ${productVariantFragment}
  query VariantList($ids: [ID!]) {
    productVariants(ids: $ids, first: 100) {
      edges {
        node {
          ...ProductVariantFields
          product {
            ...BasicProductFields
          }
        }
      }
    }
  }
`

export const productThumbnailFragment = gql`
  ${productPricingFragment}
  fragment ProductThumbnailFields on Product {
    ...ProductPricingField
    id
    slug
    name
    weight {
      unit
      value
    }
    thumbnail {
      url
      alt
    }
    thumbnail2x: thumbnail(size: 510) {
      url
    }
    category {
      id
      name
    }
  }
`

export const searchProductsQuery = gql`
  ${productThumbnailFragment}
  query SearchProducts(
    $query: String!
    $attributes: [AttributeInput]
    $pageSize: Int
    $sortBy: ProductOrder
    $after: String
    $availability: StockAvailability
    $categories: [ID]
  ) {
    products(
      filter: {
        search: $query,
        attributes: $attributes,
        stockAvailability: $availability,
        isPublished: true,
        categories: $categories,
      }
      first: $pageSize
      sortBy: $sortBy
      after: $after
    ) {
      totalCount
      edges {
        node {
          ...ProductThumbnailFields
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
    attributes(filter: {filterableInStorefront: true}, first: 100) {
      edges {
        node {
          id
          name
          slug
          values {
            id
            name
            slug
          }
        }
      }
    }
  }
`
